import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Tabs, Tab, Paper, CircularProgress, 
  Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, IconButton, Chip, TextField, Select, MenuItem,
  FormControl, FormHelperText, InputLabel, Alert, Grid, 
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Code as CodeIcon } from '@mui/icons-material';
import api from '../../services/api';

// Tool categories with corresponding colors
const TOOL_CATEGORIES = {
  therapeutic: { label: 'Therapeutic', color: 'primary' },
  safety: { label: 'Safety', color: 'error' },
  administrative: { label: 'Admin', color: 'secondary' },
  general: { label: 'General', color: 'default' },
  assessment: { label: 'Assessment', color: 'info' } // Added assessment category
};

// TabPanel component for tab content
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tool-tabpanel-${index}`}
      aria-labelledby={`tool-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TherapistToolRegistry = ({ token }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [tools, setTools] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [migrationStatus, setMigrationStatus] = useState({ loading: false, success: null, message: '' });

  // Tool form state
  const [currentTool, setCurrentTool] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [toolForm, setToolForm] = useState({
    name: '',
    category: 'therapeutic',
    description: '',
    technicalSpec: {
      name: '',
      description: '',
      parameters: {
        type: 'object',
        properties: {},
        required: []
      }
    },
    promptGuidance: {
      heading: '',
      instructions: '',
      examples: [''],
      warningsAndLimitations: ''
    },
    usageContext: {
      triggerPatterns: [''],
      avoidance: ['']
    }
  });

  // Assignment state
  const [assignToolDialogOpen, setAssignToolDialogOpen] = useState(false);
  const [selectedToolId, setSelectedToolId] = useState(null);
  const [selectedPersonaKey, setSelectedPersonaKey] = useState('');
  const [assignmentOptions, setAssignmentOptions] = useState({
    enabled: true,
    customInstructions: null
  });

  // Fetch tools and personas on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [toolsResponse, personasResponse] = await Promise.all([
          api.get('/tools', token),
          api.get('/personas', token)
        ]);
        
        setTools(toolsResponse.data || []);
        setPersonas(personasResponse.data || []);
        setError('');
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load tools and personas');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setToolForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNestedInputChange = (parentField, field, value) => {
    setToolForm(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [field]: value
      }
    }));
    // Clear error for this field
    const errorKey = `${parentField}.${field}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  const handleArrayInputChange = (parentField, childField, index, value) => {
    setToolForm(prev => {
      const newArray = [...prev[parentField][childField]];
      newArray[index] = value;
      return {
        ...prev,
        [parentField]: {
          ...prev[parentField],
          [childField]: newArray
        }
      };
    });
  };

  const addArrayItem = (parentField, childField) => {
    setToolForm(prev => {
      const newArray = [...prev[parentField][childField], ''];
      return {
        ...prev,
        [parentField]: {
          ...prev[parentField],
          [childField]: newArray
        }
      };
    });
  };

  const removeArrayItem = (parentField, childField, index) => {
    setToolForm(prev => {
      const newArray = [...prev[parentField][childField]];
      newArray.splice(index, 1);
      return {
        ...prev,
        [parentField]: {
          ...prev[parentField],
          [childField]: newArray
        }
      };
    });
  };

  // Validate the form
  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!toolForm.name) errors.name = 'Name is required';
    if (!toolForm.description) errors.description = 'Description is required';
    if (!toolForm.technicalSpec.name) errors['technicalSpec.name'] = 'Technical name is required';
    if (!toolForm.technicalSpec.description) errors['technicalSpec.description'] = 'Technical description is required';
    if (!toolForm.promptGuidance.heading) errors['promptGuidance.heading'] = 'Section heading is required';
    if (!toolForm.promptGuidance.instructions) errors['promptGuidance.instructions'] = 'Instructions are required';
    
    // Make sure technical name matches the tool name for consistency
    if (toolForm.technicalSpec.name !== toolForm.name && toolForm.name && toolForm.technicalSpec.name) {
      errors['technicalSpec.name'] = 'Technical name should match tool name';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit the form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      let result;
      
      // Format the technical spec to match expected structure
      const formattedForm = {
        ...toolForm,
        technicalSpec: {
          ...toolForm.technicalSpec,
          // Add any formatting needed for technical spec
        }
      };
      
      if (currentTool) {
        // Update existing tool
        result = await api.put(`/tools/${currentTool._id}`, formattedForm, token);
        setSuccess(`Tool "${result.data.name}" updated successfully`);
        
        // Update tools list
        setTools(tools.map(tool => 
          tool._id === currentTool._id ? result.data : tool
        ));
      } else {
        // Create new tool
        result = await api.post('/tools', formattedForm, token);
        setSuccess(`Tool "${result.data.name}" created successfully`);
        
        // Add to tools list
        setTools([...tools, result.data]);
      }
      
      // Reset form and go to tools list
      setCurrentTool(null);
      resetForm();
      setActiveTab(0);
    } catch (err) {
      console.error('Error submitting tool:', err);
      setError(err.response?.data?.msg || 'Failed to save tool');
    } finally {
      setLoading(false);
    }
  };

  // Reset the form
  const resetForm = () => {
    setToolForm({
      name: '',
      category: 'therapeutic',
      description: '',
      technicalSpec: {
        name: '',
        description: '',
        parameters: {
          type: 'object',
          properties: {},
          required: []
        }
      },
      promptGuidance: {
        heading: '',
        instructions: '',
        examples: [''],
        warningsAndLimitations: ''
      },
      usageContext: {
        triggerPatterns: [''],
        avoidance: ['']
      }
    });
    setFormErrors({});
    setCurrentTool(null);
  };

  // Edit a tool
  const handleEditTool = (tool) => {
    setCurrentTool(tool);
    
    // Ensure all required fields exist on the tool
    const editableTool = {
      ...tool,
      promptGuidance: {
        heading: tool.promptGuidance?.heading || '',
        instructions: tool.promptGuidance?.instructions || '',
        examples: tool.promptGuidance?.examples || [''],
        warningsAndLimitations: tool.promptGuidance?.warningsAndLimitations || ''
      },
      usageContext: {
        triggerPatterns: tool.usageContext?.triggerPatterns || [''],
        avoidance: tool.usageContext?.avoidance || ['']
      }
    };
    
    setToolForm(editableTool);
    setActiveTab(1);
  };

  // Delete a tool
  const handleDeleteTool = async (id) => {
    // Ask for confirmation
    if (!window.confirm('Are you sure you want to deactivate this tool? This will remove it from all personas.')) {
      return;
    }
    
    setLoading(true);
    try {
      await api.delete(`/tools/${id}`, token);
      setSuccess('Tool deactivated successfully');
      
      // Update tools list
      setTools(tools.filter(tool => tool._id !== id));
    } catch (err) {
      console.error('Error deleting tool:', err);
      setError(err.response?.data?.msg || 'Failed to delete tool');
    } finally {
      setLoading(false);
    }
  };

  // Open assign tool dialog
  const handleOpenAssignDialog = (toolId) => {
    setSelectedToolId(toolId);
    setSelectedPersonaKey('');
    setAssignmentOptions({
      enabled: true,
      customInstructions: null
    });
    setAssignToolDialogOpen(true);
  };

  // Assign tool to persona
  const handleAssignTool = async () => {
    if (!selectedToolId || !selectedPersonaKey) {
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/tools/${selectedToolId}/assign/${selectedPersonaKey}`, 
        assignmentOptions, token);
      setSuccess(`Tool assigned to "${selectedPersonaKey}" successfully`);
      
      // Close dialog
      setAssignToolDialogOpen(false);
      
      // Refresh personas to get updated tool assignments
      const personasResponse = await api.get('/personas', token);
      setPersonas(personasResponse.data || []);
    } catch (err) {
      console.error('Error assigning tool:', err);
      setError(err.response?.data?.msg || 'Failed to assign tool');
    } finally {
      setLoading(false);
    }
  };

  // Migrate existing tools from prompts.json
  const handleMigrateTools = async () => {
    setMigrationStatus({ loading: true, success: null, message: '' });
    try {
      const result = await api.post('/tools/migrate', {}, token);
      setMigrationStatus({ 
        loading: false, 
        success: result.success, 
        message: result.message 
      });
      
      if (result.success) {
        // Refresh tools
        const toolsResponse = await api.get('/tools', token);
        setTools(toolsResponse.data || []);
      }
    } catch (err) {
      console.error('Error migrating tools:', err);
      setMigrationStatus({ 
        loading: false, 
        success: false, 
        message: err.response?.data?.msg || 'Failed to migrate tools' 
      });
    }
  };

  // Render tools list
  const renderToolsList = () => {
    if (tools.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            No tools found. You can create new tools or migrate existing ones from prompts.json.
          </Typography>
          <Button 
            variant="contained" 
            color="secondary" 
            onClick={handleMigrateTools}
            disabled={migrationStatus.loading}
            sx={{ mr: 2 }}
          >
            {migrationStatus.loading ? <CircularProgress size={24} /> : 'Migrate from prompts.json'}
          </Button>
          <Button 
            variant="contained" 
            onClick={() => { resetForm(); setActiveTab(1); }}
            startIcon={<AddIcon />}
          >
            Create New Tool
          </Button>
          
          {migrationStatus.message && (
            <Alert 
              severity={migrationStatus.success ? 'success' : 'error'} 
              sx={{ mt: 2 }}
            >
              {migrationStatus.message}
            </Alert>
          )}
        </Box>
      );
    }
    
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Tools ({tools.length})</Typography>
          <Box>
            <Button 
              variant="outlined" 
              color="secondary" 
              onClick={handleMigrateTools}
              disabled={migrationStatus.loading}
              sx={{ mr: 2 }}
            >
              {migrationStatus.loading ? <CircularProgress size={24} /> : 'Migrate from prompts.json'}
            </Button>
            <Button 
              variant="contained" 
              onClick={() => { resetForm(); setActiveTab(1); }}
              startIcon={<AddIcon />}
            >
              Create New Tool
            </Button>
          </Box>
        </Box>
        
        {migrationStatus.message && (
          <Alert 
            severity={migrationStatus.success ? 'success' : 'error'} 
            sx={{ mb: 2 }}
          >
            {migrationStatus.message}
          </Alert>
        )}
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Prompt Guidance</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tools.map((tool) => (
                <TableRow key={tool._id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {tool.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={TOOL_CATEGORIES[tool.category]?.label || tool.category} 
                      color={TOOL_CATEGORIES[tool.category]?.color || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{tool.description}</TableCell>
                  <TableCell>
                    {tool.promptGuidance?.heading ? (
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        {tool.promptGuidance.heading}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No guidance defined
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      onClick={() => handleEditTool(tool)}
                      title="Edit tool"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenAssignDialog(tool._id)}
                      title="Assign to persona"
                      color="primary"
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteTool(tool._id)}
                      title="Deactivate tool"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  // Render tool form
  const renderToolForm = () => {
    return (
      <form onSubmit={handleSubmit}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {currentTool ? `Edit Tool: ${currentTool.name}` : 'Create New Tool'}
        </Typography>
        
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Basic Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Tool Name"
                    name="name"
                    value={toolForm.name}
                    onChange={handleInputChange}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      name="category"
                      value={toolForm.category}
                      onChange={handleInputChange}
                      label="Category"
                    >
                      {Object.entries(TOOL_CATEGORIES).map(([value, { label }]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={toolForm.description}
                    onChange={handleInputChange}
                    error={!!formErrors.description}
                    helperText={formErrors.description}
                    required
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Technical Specification */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Technical Specification
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Function Name"
                    value={toolForm.technicalSpec.name}
                    onChange={(e) => handleNestedInputChange('technicalSpec', 'name', e.target.value)}
                    error={!!formErrors['technicalSpec.name']}
                    helperText={formErrors['technicalSpec.name'] || 'Technical name used in function calling'}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Function Description"
                    value={toolForm.technicalSpec.description}
                    onChange={(e) => handleNestedInputChange('technicalSpec', 'description', e.target.value)}
                    error={!!formErrors['technicalSpec.description']}
                    helperText={formErrors['technicalSpec.description']}
                    required
                    multiline
                    rows={2}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Parameters (JSON)"
                    value={JSON.stringify(toolForm.technicalSpec.parameters, null, 2)}
                    onChange={(e) => {
                      try {
                        const parsedParams = JSON.parse(e.target.value);
                        handleNestedInputChange('technicalSpec', 'parameters', parsedParams);
                      } catch (err) {
                        // If JSON is invalid, just store as string temporarily
                        // We'll validate later
                      }
                    }}
                    multiline
                    rows={8}
                    InputProps={{
                      startAdornment: <CodeIcon color="action" sx={{ mr: 1 }} />,
                    }}
                  />
                  <FormHelperText>
                    Define the parameters schema in JSON format following OpenAI function calling standard
                  </FormHelperText>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Prompt Guidance */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Prompt Guidance for LLM
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Section Heading"
                    value={toolForm.promptGuidance.heading}
                    onChange={(e) => handleNestedInputChange('promptGuidance', 'heading', e.target.value)}
                    error={!!formErrors['promptGuidance.heading']}
                    helperText={formErrors['promptGuidance.heading'] || 'e.g., "GOAL SETTING (Function Calling)"'}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Instructions"
                    value={toolForm.promptGuidance.instructions}
                    onChange={(e) => handleNestedInputChange('promptGuidance', 'instructions', e.target.value)}
                    error={!!formErrors['promptGuidance.instructions']}
                    helperText={formErrors['promptGuidance.instructions'] || 'Detailed instructions for the LLM on when/how to use this tool'}
                    required
                    multiline
                    rows={6}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Examples
                  </Typography>
                  
                  {toolForm.promptGuidance.examples.map((example, index) => (
                    <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                      <TextField
                        fullWidth
                        value={example}
                        onChange={(e) => handleArrayInputChange('promptGuidance', 'examples', index, e.target.value)}
                        size="small"
                        multiline
                        rows={2}
                        placeholder="Example of using this tool"
                      />
                      <IconButton 
                        onClick={() => removeArrayItem('promptGuidance', 'examples', index)}
                        disabled={toolForm.promptGuidance.examples.length <= 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => addArrayItem('promptGuidance', 'examples')}
                  >
                    Add Example
                  </Button>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Warnings & Limitations"
                    value={toolForm.promptGuidance.warningsAndLimitations || ''}
                    onChange={(e) => handleNestedInputChange('promptGuidance', 'warningsAndLimitations', e.target.value)}
                    multiline
                    rows={3}
                    placeholder="Any warnings or limitations the LLM should be aware of"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Usage Context */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Usage Context
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Trigger Patterns
                  </Typography>
                  
                  {toolForm.usageContext.triggerPatterns.map((pattern, index) => (
                    <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                      <TextField
                        fullWidth
                        value={pattern}
                        onChange={(e) => handleArrayInputChange('usageContext', 'triggerPatterns', index, e.target.value)}
                        size="small"
                        placeholder="When to consider using this tool"
                      />
                      <IconButton 
                        onClick={() => removeArrayItem('usageContext', 'triggerPatterns', index)}
                        disabled={toolForm.usageContext.triggerPatterns.length <= 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => addArrayItem('usageContext', 'triggerPatterns')}
                  >
                    Add Trigger Pattern
                  </Button>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Avoidance Patterns
                  </Typography>
                  
                  {toolForm.usageContext.avoidance.map((pattern, index) => (
                    <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                      <TextField
                        fullWidth
                        value={pattern}
                        onChange={(e) => handleArrayInputChange('usageContext', 'avoidance', index, e.target.value)}
                        size="small"
                        placeholder="When to avoid using this tool"
                      />
                      <IconButton 
                        onClick={() => removeArrayItem('usageContext', 'avoidance', index)}
                        disabled={toolForm.usageContext.avoidance.length <= 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => addArrayItem('usageContext', 'avoidance')}
                  >
                    Add Avoidance Pattern
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Form Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => { resetForm(); setActiveTab(0); }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                type="submit"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : (currentTool ? 'Update Tool' : 'Create Tool')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    );
  };

  // Render assign tool dialog
  const renderAssignToolDialog = () => {
    const selectedTool = tools.find(tool => tool._id === selectedToolId);
    
    return (
      <Dialog open={assignToolDialogOpen} onClose={() => setAssignToolDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Tool to Persona
        </DialogTitle>
        <DialogContent>
          {selectedTool && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                Tool: {selectedTool.name}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Category:</strong> {TOOL_CATEGORIES[selectedTool.category]?.label || selectedTool.category}
              </Typography>
              <Typography variant="body2">
                <strong>Description:</strong> {selectedTool.description}
              </Typography>
            </Box>
          )}
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Persona</InputLabel>
            <Select
              value={selectedPersonaKey}
              onChange={(e) => setSelectedPersonaKey(e.target.value)}
              label="Select Persona"
            >
              {personas.map((persona) => (
                <MenuItem key={persona.key} value={persona.key}>
                  {persona.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Options
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <input
                type="checkbox"
                checked={assignmentOptions.enabled}
                onChange={(e) => setAssignmentOptions(prev => ({ ...prev, enabled: e.target.checked }))}
                id="enabledCheckbox"
              />
              <label htmlFor="enabledCheckbox" style={{ marginLeft: '8px' }}>
                Enable tool for this persona
              </label>
            </Box>
            <TextField
              fullWidth
              label="Custom Instructions (Optional)"
              multiline
              rows={4}
              value={assignmentOptions.customInstructions || ''}
              onChange={(e) => setAssignmentOptions(prev => ({ ...prev, customInstructions: e.target.value || null }))}
              placeholder="Override default instructions for this persona (leave empty to use default)"
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignToolDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssignTool} 
            variant="contained" 
            disabled={!selectedPersonaKey || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Assign Tool'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <div>
      {/* Notifications */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Tool Registry" />
          <Tab label="Create/Edit Tool" />
        </Tabs>
      </Box>
      
      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        {loading && activeTab === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          renderToolsList()
        )}
      </TabPanel>
      
      <TabPanel value={activeTab} index={1}>
        {renderToolForm()}
      </TabPanel>
      
      {/* Assign Tool Dialog */}
      {renderAssignToolDialog()}
    </div>
  );
};

export default TherapistToolRegistry;
